import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EMPTY_FILTER, type RecordingFilter } from './recordingFilterRules';

/**
 * Keep a recording filter in the URL.
 *
 * Filters held in component state have three problems that only show up once
 * people rely on them:
 *
 *   - **Back doesn't undo a filter.** Narrowing to a window with no
 *     recordings looks like a dead end, and the instinct is to press Back —
 *     which leaves the page entirely, because nothing about the filter was
 *     ever in the history. That was reported as a bug twice.
 *   - **A refresh loses the view.** Reloading mid-investigation dumps you
 *     back at "all time".
 *   - **You can't send it to anyone.** "Look at Kebebew's parathyroidectomies
 *     that Tuesday afternoon" is a link, if the filter is in the URL.
 *
 * Each change pushes a history entry, so Back walks back through the
 * narrowing and only leaves the page once there is nothing left to undo.
 *
 * Scoped by `key` so two filtered lists on one page don't fight over the same
 * params. Not for dialogs — a modal's filter has no business in the URL.
 */

const LIST_SEP = ',';

function readList(params: URLSearchParams, name: string): string[] {
  const raw = params.get(name);
  if (!raw) return [];
  return raw.split(LIST_SEP).map((s) => s.trim()).filter(Boolean);
}

export function useFilterParams(
  key: string,
): [RecordingFilter, (next: RecordingFilter) => void] {
  const [params, setParams] = useSearchParams();
  const p = (name: string) => `${key}_${name}`;

  const filter = useMemo<RecordingFilter>(
    () => ({
      participants: readList(params, p('who')),
      devices: readList(params, p('dev')),
      procedures: readList(params, p('proc')),
      from: params.get(p('from')) ?? '',
      to: params.get(p('to')) ?? '',
      // Absent stays absent: only the two written values become an opinion,
      // so a link that never mentioned this dimension doesn't acquire one.
      processingFailed:
        params.get(p('failed')) === 'true'
          ? true
          : params.get(p('failed')) === 'false'
            ? false
            : undefined,
    }),
    // `params` is a new object each render but compares by its string form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString(), key],
  );

  const setFilter = useCallback(
    (next: RecordingFilter) => {
      const updated = new URLSearchParams(params);
      const put = (name: string, value: string) => {
        if (value) updated.set(p(name), value);
        else updated.delete(p(name));
      };
      put('who', next.participants.join(LIST_SEP));
      put('dev', next.devices.join(LIST_SEP));
      put('proc', next.procedures.join(LIST_SEP));
      put('from', next.from);
      put('to', next.to);
      put(
        'failed',
        next.processingFailed === undefined ? '' : String(next.processingFailed),
      );
      // Push, not replace: the whole point is that Back undoes the narrowing
      // instead of leaving the page.
      setParams(updated);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString(), setParams, key],
  );

  return [filter, setFilter];
}

/** The unfiltered value, for callers that need to compare. */
export const NO_FILTER = EMPTY_FILTER;
